import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Papa from "papaparse";
import { Download, FileText, Filter } from "lucide-react";

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [type, setType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    const params = {};
    if (type) params.type_ = type;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/reports", { params }).then(({ data }) => setReports(data));
  }, [type, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q) return reports;
    const s = q.toLowerCase();
    return reports.filter((r) => (r.report_number + " " + (r.manager_name || "")).toLowerCase().includes(s));
  }, [reports, q]);

  const exportCsv = () => {
    const rows = filtered.map((r) => ({
      report_number: r.report_number, type: r.type, business_date: r.business_date,
      manager: r.manager_name, start: r.start_time, completed: r.completed_time,
      actual: r.actual_time, completion_pct: r.completion_pct, status: r.status,
    }));
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "reports.csv"; a.click();
  };

  return (
    <div className="space-y-6" data-testid="reports-root">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Archive</div>
          <h1 className="font-heading text-3xl sm:text-4xl">Reports & History</h1>
        </div>
        <button className="hos-btn-outline flex items-center gap-2" onClick={exportCsv} data-testid="reports-export-csv">
          <Download size={16}/> Export CSV
        </button>
      </div>

      <div className="hos-card p-4 flex flex-wrap gap-3 items-end">
        <Filter size={16} className="text-[color:var(--gold)] mb-3"/>
        <select className="hos-input max-w-[180px]" value={type} onChange={(e) => setType(e.target.value)} data-testid="filter-type">
          <option value="">All types</option><option value="opening">Opening</option><option value="closing">Closing</option>
        </select>
        <input type="date" className="hos-input max-w-[180px]" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from"/>
        <input type="date" className="hos-input max-w-[180px]" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to"/>
        <input className="hos-input flex-1 min-w-[200px]" placeholder="Search by report number or manager…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="filter-search"/>
      </div>

      <div className="hos-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[color:var(--gold)] label-caps">
            <tr>
              <th className="p-4">Report #</th><th>Type</th><th>Date</th><th>Manager</th>
              <th>Completion</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-[color:var(--border)] hover:bg-[color:var(--bg-surface-2)]">
                <td className="p-4 font-mono">{r.report_number}</td>
                <td className="capitalize">{r.type}</td>
                <td>{r.business_date}</td>
                <td>{r.manager_name}</td>
                <td>{r.completion_pct}%</td>
                <td><span className="status-pill status-open">{r.status}</span></td>
                <td className="pr-4"><Link to={`/reports/${r.id}`} className="text-[color:var(--gold)] flex items-center gap-1" data-testid={`view-report-${r.id}`}><FileText size={14}/> View</Link></td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-[color:var(--text-secondary)]">No reports found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
